import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import axios from "axios";
import api from "@/api/axios";
import HeaderBar from "@/components/HeaderBar";
import ResponsiveSection from "@/components/ResponsiveSection";
import { useAuth } from "@/context/AuthContext";

export default function LoginPage() {
  const [telegramId, setTelegramId] = useState("");
  const [password, setPassword] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [loginMethod, setLoginMethod] = useState<"password" | "verifyCode">("password");
  const reduceMotion = useReducedMotion();
  const { setToken } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!telegramId) {
      alert("请填写Telegram用户名");
      return;
    }

    if (loginMethod === "password" && !password) {
      alert("请填写密码");
      return;
    }

    if (loginMethod === "verifyCode" && !verifyCode) {
      alert("请填写验证码");
      return;
    }

    try {
      const endpoint = loginMethod === "password" ? "/api/login" : "/api/login_with_code";
      const data = loginMethod === "password" 
        ? { telegram_id: telegramId, password }
        : { telegram_id: telegramId, verify_code: verifyCode };

      const res = await api.post(endpoint, data);
      if (res.data.success) {
        const token = res.data.token;
        document.cookie = `token=${token}; path=/; max-age=${7 * 86400}`;
        setToken(token);
        navigate("/dashboard");
      } else {
        alert(res.data.message || "登录失败");
      }
    } catch (err) {
      console.error(err);
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      alert(typeof detail === "string" ? detail : "登录失败，请稍后重试");
    }
  };

  const openTelegramBot = () => {
    window.open("https://t.me/TGAutoCheckinbot?start=login", "_blank");
  };

  return (
    <div className="public-page min-h-screen flex flex-col">
      <HeaderBar />
      <ResponsiveSection>
        <motion.form
          layout
          noValidate
          transition={{ layout: { duration: reduceMotion ? 0 : 0.25 } }}
          onSubmit={handleLogin}
          className="public-auth-form"
        >
          <h1>登录</h1>
          
          <div className="public-auth-switch" data-method={loginMethod} role="group" aria-label="登录方式">
            <button
              type="button"
              aria-pressed={loginMethod === "password"}
              className={loginMethod === "password" ? "is-active" : ""}
              onClick={() => setLoginMethod("password")}
            >
              密码登录
            </button>
            <button
              type="button"
              aria-pressed={loginMethod === "verifyCode"}
              className={loginMethod === "verifyCode" ? "is-active" : ""}
              onClick={() => setLoginMethod("verifyCode")}
            >
              验证码登录
            </button>
          </div>

          <label className="public-auth-field">Telegram 用户名<input
            type="text"
            autoComplete="username"
            value={telegramId}
            onChange={(e) => setTelegramId(e.target.value)}
            placeholder="Telegram 用户名 (@开头)"
            required
          /></label>

          <div className="public-auth-method-stage">
          <AnimatePresence mode="sync" initial={false}>
            {loginMethod === "password" ? (
              <motion.div key="password" className="public-auth-method" initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: reduceMotion ? 0 : -6 }} transition={{ duration: reduceMotion ? 0 : 0.18 }}>
                <label className="public-auth-field">登录密码<input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="登录密码"
                  required={loginMethod === "password"}
                /></label>
              </motion.div>
            ) : (
              <motion.div key="verifyCode" className="public-auth-method public-auth-code-block" initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: reduceMotion ? 0 : -6 }} transition={{ duration: reduceMotion ? 0 : 0.18 }}>
                <label className="public-auth-field">验证码<div className="public-auth-inline">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value)}
                    placeholder="验证码 (与机器人对话获取)"
                    required={loginMethod === "verifyCode"}
                  />
                  <button
                    type="button"
                    onClick={openTelegramBot}
                    className="public-auth-secondary"
                  >
                    获取验证码
                  </button>
                </div></label>
                <p className="public-auth-hint">
                  请在 Telegram 与
                  <a
                    href="https://t.me/TGAutoCheckinbot"
                    target="_blank"
                    className="text-brand hover:underline mx-1"
                    rel="noreferrer"
                  >
                    @TGAutoCheckinbot
                  </a>
                  机器人对话获取验证码
                </p>
              </motion.div>
            )}
          </AnimatePresence>
          </div>

          <button
            type="submit"
            className="public-auth-submit"
          >
            登录
          </button>
          <p className="public-auth-footer">
            还没有账号？
            <Link to="/register">
              立即注册
            </Link>
          </p>
        </motion.form>
      </ResponsiveSection>
    </div>
  );
}
